'use client';

import { ApolloClient, InMemoryCache, from, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { HttpLink } from '@apollo/client/link/http';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { refreshAccessToken } from '@/services/tokenRefresh';
import { PUBLIC_ROUTES } from '@/configs/routes';

export interface CreateApolloClientOptions {
  token: string;
  refreshToken?: string;
  onTokenRefresh?: (token: string, refreshToken: string) => void;
}

export const createApolloClient = (
  options: CreateApolloClientOptions | string,
): ApolloClient<any> | null => {
  const { token, refreshToken, onTokenRefresh } =
    typeof options === 'string'
      ? { token: options, refreshToken: undefined, onTokenRefresh: undefined }
      : options;

  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URI,
  });

  // Track pending requests so we can abort them on demand
  // Exported below via abortAllPendingGraphQL
  const abortableLink = new ApolloLink((operation, forward) => {
    const controller = new AbortController();
    pendingRequestControllers.add(controller);

    operation.setContext(({ fetchOptions = {} }) => ({
      fetchOptions: {
        ...fetchOptions,
        signal: controller.signal,
      },
    }));

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (value) => observer.next(value),
        error: (err) => {
          pendingRequestControllers.delete(controller);
          // treat fetch aborts as a non-error completion to avoid noisy ApolloError
          const isAbortError =
            !!err &&
            (err.name === 'AbortError' ||
              err.message?.includes('signal is aborted') ||
              err.message?.includes('aborted'));
          if (isAbortError) {
            observer.complete();
            return;
          }
          observer.error(err);
        },
        complete: () => {
          pendingRequestControllers.delete(controller);
          observer.complete();
        },
      });

      return () => {
        // On unsubscribe, ensure we stop the underlying fetch
        pendingRequestControllers.delete(controller);
        try {
          controller.abort('Cancelled by user');
        } catch {}
        subscription.unsubscribe();
      };
    });
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (
        networkError &&
        'statusCode' in networkError &&
        networkError.statusCode === 401
      ) {
        if (refreshToken && onTokenRefresh) {
          // Create a new observable for the refresh operation
          return new Observable((observer) => {
            refreshAccessToken(refreshToken)
              .then((response) => {
                onTokenRefresh(response.token, response.refreshToken);

                // Update the operation context with the new token
                operation.setContext({
                  headers: {
                    ...operation.getContext().headers,
                    Authorization: `Bearer ${response.token}`,
                  },
                });

                // Retry the request
                const subscriber = forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                });

                return () => {
                  if (subscriber) subscriber.unsubscribe();
                };
              })
              .catch((error) => {
                // Redirect to login if refresh fails
                window.location.href = PUBLIC_ROUTES.HOME;
                observer.error(error);
              });
          });
        }
      }
    },
  );

  return new ApolloClient({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URI,
    link: from([errorLink, authLink, abortableLink, httpLink]),
    cache: new InMemoryCache(),
  });
};

// Registry of AbortControllers for in-flight GraphQL requests
const pendingRequestControllers = new Set<AbortController>();

// Abort all current GraphQL requests initiated via this Apollo client
export const abortAllPendingGraphQL = () => {
  for (const controller of pendingRequestControllers) {
    try {
      controller.abort();
    } catch {}
  }
  pendingRequestControllers.clear();
};
